import api from '../api';
import { createOrder } from '../api';
import { getCustomer, updateProfile, saveCustomer } from './customerService';

// Гостевой checkout не поддерживается (продуктовое решение, FBS_PHASE1_SECURITY_AND_FLOW_PLAN.md,
// Task A) — POST /api/orders/create теперь требует customer JWT. Бросаем отдельный тип ошибки,
// чтобы страница чекаута могла отличить "не вошли" от обычной ошибки сети/валидации и показать
// понятное сообщение с редиректом на экран входа, а не сырую ошибку.
export class CheckoutAuthRequiredError extends Error {
  constructor() {
    super('Для оформления заказа войдите по номеру телефона');
    this.name = 'CheckoutAuthRequiredError';
  }
}

// Ключ 'customerAccessToken' — тот же, что использует customerService.js (единственный
// источник правды для его записи/очистки); здесь только читаем, чтобы не заводить второй
// геттер ради одной проверки.
function hasCustomerSession() {
  return Boolean(getCustomer() && localStorage.getItem('customerAccessToken'));
}

export function buildShipments(items) {
  const groups = {};
  items.forEach(item => {
    const sellerId = item.seller_id || 'unknown';
    if (!groups[sellerId]) {
      groups[sellerId] = {
        seller_id: sellerId,
        totalWeight: 0,
        maxLength: 0,
        maxWidth: 0,
        totalHeight: 0,
        totalPrice: 0,
      };
    }
    const g = groups[sellerId];
    const qty = item.quantity || 1;
    const w = item.weight;
    const l = item.length;
    const wd = item.width;
    const h = item.height;
    const price = (item.price || 0) * qty;

    if (w == null || l == null || wd == null || h == null) throw new Error('Для расчёта доставки не заполнены масса и габариты товара');
    g.totalWeight += w * qty;
    g.totalPrice += price;
    if (l > g.maxLength) g.maxLength = l;
    if (wd > g.maxWidth) g.maxWidth = wd;
    g.totalHeight += h * qty;
  });

  return Object.values(groups).map(g => ({
    seller_id: g.seller_id,
    totalWeight: g.totalWeight,
    dimensions: {
      length: g.maxLength || 25,
      width: g.maxWidth || 20,
      height: g.totalHeight || 5,
    },
    totalPrice: g.totalPrice,
  }));
}

export async function calculateDelivery(shipments, pointId) {
  const requests = shipments.map(s =>
    api.post('/api/delivery/calculate', {
      toPointId: pointId,
      totalWeight: s.totalWeight,
      totalPrice: s.totalPrice,
      dimensions: s.dimensions,
    })
  );
  const results = await Promise.all(requests);
  let totalCost = 0;
  let maxDays = 0;
  results.forEach(r => {
    if (r.data.price) totalCost += Math.ceil(r.data.price);
    if (r.data.deliveryDays && r.data.deliveryDays > maxDays) maxDays = r.data.deliveryDays;
  });
  return { cost: totalCost, days: maxDays > 0 ? maxDays : null };
}

const PENDING_CHECKOUT = 'maxmarket.checkout.pending';
export function pendingCustomerCheckout() {
  try { const saved = JSON.parse(sessionStorage.getItem(PENDING_CHECKOUT)); return saved?.phone === getCustomer()?.phone ? saved : null; }
  catch { return null; }
}
export function clearPendingCheckout(orderId) {
  const saved = pendingCustomerCheckout();
  if (saved && (!orderId || String(saved.orderId) === String(orderId))) sessionStorage.removeItem(PENDING_CHECKOUT);
}
export async function cartAfterPaidCheckout(orderId, items) {
  if (String(pendingCustomerCheckout()?.orderId) !== String(orderId)) return null;
  const order = (await api.get(`/api/orders/customer/${orderId}`)).data;
  const bought = new Map();
  const selectedLines = pendingCustomerCheckout()?.selectedLines;
  for (const item of order.items || []) bought.set(Number(item.variantId), (bought.get(Number(item.variantId)) || 0) + item.quantity);
  return items.flatMap(item => {
    const line = selectedLines?.find(line => line.uniqueId === item.uniqueId && Number(line.variantId) === Number(item.selectedVariantId));
    if (selectedLines && !line) return [item];
    const id = Number(item.selectedVariantId), count = Math.min(item.quantity, bought.get(id) || 0, line?.quantity ?? item.quantity);
    bought.set(id, (bought.get(id) || 0) - count);
    return count === item.quantity ? [] : [{ ...item, quantity: item.quantity - count }];
  });
}
export class CheckoutAmountChangedError extends Error {
  constructor(orderId, amountMinor) { super(`Итог заказа изменился: ${(amountMinor / 100).toFixed(2)} ₽. Проверьте заказ перед оплатой.`); this.orderId = orderId; this.amountMinor = amountMinor; }
}
export async function payCustomerOrder(orderId, expectedAmountMinor, savedCardId) {
  if (!hasCustomerSession()) throw new CheckoutAuthRequiredError();
  const response = savedCardId ? await api.post(`/api/payment/orders/${orderId}/charge`, { cardId: savedCardId, expectedAmountMinor }) : await api.post('/api/payment/init', { orderId: String(orderId), expectedAmountMinor });
  if (!response.data?.PaymentURL) throw new Error('Банк не вернул адрес оплаты. Проверьте статус заказа.');
  return response.data.PaymentURL;
}

export async function createOrdersAndPay(items, customerData, delivery, _saveCard, expectedAmountMinor, savedCardId) {
  if (!hasCustomerSession()) {
    throw new CheckoutAuthRequiredError();
  }

  const orderItems = items.map(item => ({
    product_id: item.id,
    variant_id: item.selectedVariantId || null,
    quantity: item.quantity || 1,
  }));

  if (pendingCustomerCheckout()) throw new Error('У вас есть незавершённое оформление. Продолжите его из «Моих заказов».');
  if (!customerData.name?.trim()) throw new Error('Укажите ваше имя');
  if (getCustomer()?.name !== customerData.name.trim()) {
    await updateProfile({ name: customerData.name.trim() });
    saveCustomer({ ...getCustomer(), name: customerData.name.trim() });
  }
  if (pendingCustomerCheckout()) throw new Error('Оформление уже начато. Продолжите из «Моих заказов».');
  // A lost create response must not issue another stock reservation automatically.
  const selectedLines = items.map(item => ({ uniqueId: item.uniqueId, variantId: item.selectedVariantId, quantity: item.quantity }));
  sessionStorage.setItem(PENDING_CHECKOUT, JSON.stringify({ phone: getCustomer().phone, stage: 'creating', selectedLines }));
  let res;
  try { res = await createOrder({
    customer_name: customerData.name,
    customer_phone: customerData.phone,
    address: delivery.method === 'PICKUP_POINT' ? delivery.pickupPoint?.address : [delivery.city, delivery.street, delivery.house, delivery.apartment].filter(Boolean).join(', '),
    delivery,
    items: orderItems,
  }); } catch (error) {
    if (error.response?.status >= 400 && error.response?.status < 500) clearPendingCheckout();
    throw error;
  }

  const customerOrderId = res.data.customerOrderId;
  sessionStorage.setItem(PENDING_CHECKOUT, JSON.stringify({ phone: getCustomer().phone, stage: 'created', orderId: customerOrderId, expectedAmountMinor, selectedLines }));
  const saved = (await api.get(`/api/orders/customer/${customerOrderId}`)).data;
  const actualMinor = Math.round((Number(saved.totalAmount || 0) + Number(saved.deliveryCost || 0)) * 100);
  if (Number.isSafeInteger(expectedAmountMinor) && actualMinor !== expectedAmountMinor) throw new CheckoutAmountChangedError(customerOrderId, actualMinor);
  return payCustomerOrder(customerOrderId, actualMinor, savedCardId);
}
